/**
 * What a Supabase auth event should do to a portal.
 *
 * Shared by every Supabase-authenticated portal. This is deliberately pure and
 * React-free: the portals' `AuthProvider`s differ (the Client Portal's context
 * carries `actor` and `organizations` from its bootstrap; the admin portals'
 * do not), but the rules below are the same for all of them, and duplicating
 * them is what let Cascade and MPF keep a bug the Client Portal had fixed.
 *
 * Supabase does not only emit `SIGNED_IN` when somebody signs in. It emits it
 * again from `_recoverAndRefresh()` — which runs on every `visibilitychange`
 * back to visible, and again when the client initializes — and it rebroadcasts
 * other tabs' events over a BroadcastChannel. Treating all of those as a fresh
 * foreground login is what made returning to the tab tear the workspace down and
 * rebuild it.
 *
 * So the event name alone cannot decide anything. The decision is: has the
 * *identity* changed, and is this identity already approved? Everything else is
 * background confirmation of something the portal already knows.
 *
 * This is a pure function so the whole matrix is testable without a browser.
 */

export type AuthEventDecision =
  /** Nothing to do — the event carries no access consequence. */
  | { kind: 'ignore' }
  /**
   * Re-check access. `blocking: true` may replace the UI with the verification
   * screen; `blocking: false` must leave whatever is on screen alone.
   */
  | { kind: 'verify'; blocking: boolean }
  /** A different person is now signed in: drop all user-scoped state, then verify. */
  | { kind: 'switch-identity' }
  /** Signed out: drop all user-scoped state and stop. */
  | { kind: 'sign-out' };

export interface AuthEventContext {
  /** The Supabase event name. */
  event: string;
  /** The user id carried by the event, if any. */
  nextUserId: string | null;
  /** The user id the portal last saw, if any. */
  knownUserId: string | null;
  /** The user id currently holding an `allowed` access decision, if any. */
  allowedUserId: string | null;
}

export function decideAuthEvent({
  event,
  nextUserId,
  knownUserId,
  allowedUserId,
}: AuthEventContext): AuthEventDecision {
  if (event === 'SIGNED_OUT') return { kind: 'sign-out' };

  // A genuinely different person. This is the only auth event that may discard
  // the previous user's cached data, and it must.
  if (nextUserId && knownUserId && nextUserId !== knownUserId) {
    return { kind: 'switch-identity' };
  }

  switch (event) {
    // The password recovery flow swaps the whole surface for the reset form;
    // access verification has nothing to contribute to it.
    case 'PASSWORD_RECOVERY':
      return { kind: 'ignore' };

    // A new token for the same person. Confirm access in the background so a
    // revocation is still noticed, but never blank the screen for it.
    case 'TOKEN_REFRESHED':
    case 'USER_UPDATED':
      return { kind: 'verify', blocking: false };

    case 'SIGNED_IN':
    case 'INITIAL_SESSION': {
      // Already approved for exactly this user: tab restoration, a repeat
      // event, or another tab's broadcast. Confirm quietly.
      if (nextUserId && nextUserId === allowedUserId) {
        return { kind: 'verify', blocking: false };
      }
      return { kind: 'verify', blocking: true };
    }

    default:
      return { kind: 'ignore' };
  }
}

/**
 * Whether a failed background verification should be allowed to revoke access.
 *
 * A verification that never reached the API, or that failed at the
 * infrastructure layer, says nothing about whether this user still has access —
 * so during revalidation it must not be able to destroy a working portal. An
 * explicit 401/403, or a successful response reporting no access, is
 * authoritative and always applies.
 *
 * `status` is undefined when the request itself threw (offline, DNS, CORS).
 */
export function isAuthoritativeDenial(status: number | undefined): boolean {
  if (status === undefined) return false;
  if (status === 401 || status === 403) return true;
  // 5xx (including the Worker's own `authorization_unavailable`) and 429 are
  // outages, not decisions.
  return status < 500 && status !== 429;
}
